import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import moment from 'moment';

import GatewayConsoleClient, { BrokerConsoleResponse } from './gatewayConsoleClient';
import { useTranslation } from 'react-i18next';
import { GATEWAY_CONNECTED, GATEWAY_CONNECTION_TIME, GATEWAY_NOT_CONNECTED } from 'attrs/gateways';

type GatewayConsoleCommand = {
  baseCommand: string;
  description: string;
  fullCommand: string;
};

type GatewayConsoleEntry = {
  id: string;
  tone: 'command' | 'error' | 'response' | 'system';
  value: string;
};

type GatewayConsoleProps = {
  gatewayId: string;
  gatewayLastSeen?: string;
};

type GatewayStatusTone = 'connected' | 'connecting' | 'disconnected' | 'error' | 'offline';

const CONSOLE_BACKGROUND = '#4C4D57';
const CONSOLE_FOREGROUND = '#FFF7F7';
const MUTED_FOREGROUND = '#C9CCD6';

function getRuntimeConfiguration() {
  const configuration = (
    window as Window & {
      configuration?: {
        apiUrl?: string;
      };
    }
  ).configuration;

  return configuration || {};
}

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '');
}

function buildWsTokenUrl(apiUrl: string) {
  return `${trimTrailingSlash(apiUrl)}/v1/ws/token`;
}

function buildWsConsoleUrl(apiUrl: string) {
  const normalizedApiUrl = trimTrailingSlash(apiUrl);
  const wsProtocolUrl = normalizedApiUrl.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  return `${wsProtocolUrl}/v1/ws/console`;
}

function createConsoleEntry(tone: GatewayConsoleEntry['tone'], value: string): GatewayConsoleEntry {
  return {
    id: `${tone}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    tone,
    value
  };
}

function normalizeSpaces(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}

function deriveBaseCommand(commandTemplate: string) {
  const tokens = commandTemplate.split(' ');
  const collectedTokens: string[] = [];

  tokens.forEach(token => {
    if (token.startsWith('<') || token.startsWith('[') || token.startsWith('--')) {
      return;
    }

    if (collectedTokens.length === 0 || !collectedTokens.includes(token)) {
      collectedTokens.push(token);
    }
  });

  return collectedTokens.join(' ');
}

function parseHelpCommands(helpResponse: string) {
  const lines = helpResponse.split('\n');
  const commands: GatewayConsoleCommand[] = [
    {
      baseCommand: '--help',
      description: '',
      fullCommand: '--help'
    }
  ];

  const commandsStartIndex = lines.findIndex(line => line.trim() === 'Commands:');
  const commandsEndIndex = lines.findIndex(line => line.startsWith('Run "GTW V3'));

  if (commandsStartIndex === -1) {
    return commands;
  }

  const relevantLines = lines.slice(commandsStartIndex + 1, commandsEndIndex === -1 ? undefined : commandsEndIndex);
  let currentCommand = '';
  let currentDescription = '';

  const flushCommand = () => {
    const fullCommand = normalizeSpaces(currentCommand);
    const description = normalizeSpaces(currentDescription);

    if (fullCommand) {
      commands.push({
        baseCommand: deriveBaseCommand(fullCommand),
        description,
        fullCommand
      });
    }

    currentCommand = '';
    currentDescription = '';
  };

  relevantLines.forEach(line => {
    if (/^ {2}\S/.test(line)) {
      flushCommand();
      currentCommand = line.trim();
      return;
    }

    if (/^ {4}\S/.test(line)) {
      currentDescription = `${currentDescription} ${line.trim()}`.trim();
      return;
    }

    if (line.trim() === '') {
      flushCommand();
    }
  });

  flushCommand();

  return commands.filter(
    (command, index, array) =>
      array.findIndex(
        entry => entry.fullCommand === command.fullCommand && entry.baseCommand === command.baseCommand
      ) === index
  );
}

function isKnownGtwCommand(command: string, commandDefinitions: GatewayConsoleCommand[]) {
  const normalizedCommand = normalizeSpaces(command);

  if (!normalizedCommand.startsWith('gtw')) {
    return true;
  }

  const gtwCommand = normalizeSpaces(normalizedCommand.slice(3));

  if (!gtwCommand) {
    return false;
  }

  return commandDefinitions.some(definition => {
    const normalizedBase = normalizeSpaces(definition.baseCommand);
    const normalizedTemplate = normalizeSpaces(definition.fullCommand);

    return (
      gtwCommand === normalizedBase ||
      gtwCommand === normalizedTemplate ||
      gtwCommand.startsWith(`${normalizedBase} `) ||
      gtwCommand.startsWith(`${normalizedTemplate} `)
    );
  });
}

function formatBrokerResponse(payload: BrokerConsoleResponse | string) {
  if (typeof payload === 'string') {
    return payload;
  }

  if (payload?.error && typeof payload.error === 'object' && 'message' in payload.error) {
    const errorMessage = payload.error.message;

    if (typeof errorMessage === 'string' && errorMessage.trim()) {
      return errorMessage;
    }
  }

  if (typeof payload.response === 'string' && payload.response.trim()) {
    return payload.response;
  }

  return '';
}

function getGatewayConnectionStatus(lastSeenParam?: string) {
  const lastSeen = moment(lastSeenParam || null);
  const lastSeenDiff = moment().diff(lastSeen, 'minutes');
  return lastSeenDiff <= GATEWAY_CONNECTION_TIME ? GATEWAY_CONNECTED : GATEWAY_NOT_CONNECTED;
}

export default function GatewayConsole({ gatewayId, gatewayLastSeen }: GatewayConsoleProps) {
  const { t } = useTranslation();
  const consoleViewportRef = useRef<HTMLDivElement | null>(null);
  const consoleRootRef = useRef<HTMLDivElement | null>(null);
  const suggestionsListRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef<GatewayConsoleClient | null>(null);
  const historyIndexRef = useRef<number>(-1);
  const [commandDefinitions, setCommandDefinitions] = useState<GatewayConsoleCommand[]>([]);
  const [entries, setEntries] = useState<GatewayConsoleEntry[]>([
    createConsoleEntry('system', t('gateway_details.console.messages.connecting_background'))
  ]);
  const [inputValue, setInputValue] = useState('');
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [isAutocompleteVisible, setIsAutocompleteVisible] = useState(true);
  const [status, setStatus] = useState<{ message: string; tone: GatewayStatusTone }>({
    message: t('gateway_details.console.status.connecting'),
    tone: 'connecting'
  });
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [isSendingCommand, setIsSendingCommand] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const runtimeConfiguration = getRuntimeConfiguration();
  const apiUrl = runtimeConfiguration.apiUrl || '';
  const wsTokenUrl = useMemo(() => buildWsTokenUrl(apiUrl), [apiUrl]);
  const wsConsoleUrl = useMemo(() => buildWsConsoleUrl(apiUrl), [apiUrl]);

  const appendEntry = useCallback((tone: GatewayConsoleEntry['tone'], value: string) => {
    setEntries(previousEntries => [...previousEntries, createConsoleEntry(tone, value)]);
  }, []);

  const connectInBackground = useCallback(async () => {
    const accessToken = window.localStorage.getItem('accessToken');

    if (!gatewayId) {
      setStatus({
        message: t('gateway_details.console.status.awaiting_gateway'),
        tone: 'connecting'
      });
      return;
    }

    if (!apiUrl) {
      setStatus({
        message: t('gateway_details.console.status.api_url_missing'),
        tone: 'error'
      });
      appendEntry('error', t('gateway_details.console.messages.api_url_missing'));
      return;
    }

    if (!accessToken) {
      setStatus({
        message: t('gateway_details.console.status.access_token_missing'),
        tone: 'error'
      });
      appendEntry('error', t('gateway_details.console.messages.access_token_missing'));
      return;
    }

    setStatus({
      message: t('gateway_details.console.status.connecting'),
      tone: 'connecting'
    });

    const client = new GatewayConsoleClient({
      accessToken,
      wsConsoleUrl,
      wsTokenUrl,
      onClose: event => {
        setStatus({
          message: t('gateway_details.console.status.disconnected_code', { code: event.code }),
          tone: 'disconnected'
        });
        appendEntry('system', t('gateway_details.console.messages.websocket_closed'));
      },
      onError: () => {
        setStatus({
          message: t('gateway_details.console.status.socket_error'),
          tone: 'error'
        });
      },
      onMessage: payload => {
        appendEntry('response', formatBrokerResponse(payload));
      },
      onOpen: () => {
        setStatus({
          message: t('gateway_details.console.status.connected'),
          tone: 'connected'
        });
      }
    });

    clientRef.current = client;

    try {
      await client.connect();
      appendEntry('system', t('gateway_details.console.messages.connection_ready', { gatewayId }));

      const helpResponse = await client.sendCommand({
        command: 'gtw --help',
        gatewayId
      });

      const formattedHelpResponse = formatBrokerResponse(helpResponse);
      appendEntry('command', 'gtw --help');

      if (formattedHelpResponse) {
        setCommandDefinitions(parseHelpCommands(formattedHelpResponse));
        appendEntry('response', formattedHelpResponse);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('gateway_details.console.messages.connection_failed_unknown');
      setStatus({
        message: t('gateway_details.console.status.connection_failed'),
        tone: 'error'
      });
      appendEntry('error', errorMessage);
    }
  }, [apiUrl, appendEntry, gatewayId, t, wsConsoleUrl, wsTokenUrl]);

  useEffect(() => {
    connectInBackground();

    return () => {
      clientRef.current?.disconnect();
    };
  }, [connectInBackground]);

  useEffect(() => {
    if (consoleViewportRef.current) {
      consoleViewportRef.current.scrollTop = consoleViewportRef.current.scrollHeight;
    }
  }, [entries]);

  useEffect(() => {
    const handleDocumentPointerDown = (event: MouseEvent) => {
      if (!consoleRootRef.current?.contains(event.target as Node)) {
        setIsAutocompleteVisible(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentPointerDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentPointerDown);
    };
  }, []);

  const gtwSuggestions = useMemo(() => {
    const normalizedInput = normalizeSpaces(inputValue);

    if (!normalizedInput.startsWith('gtw')) {
      return [];
    }

    const searchTerm = normalizeSpaces(normalizedInput.slice(3)).toLowerCase();

    return commandDefinitions.filter(definition => {
      const fullCommand = definition.fullCommand.toLowerCase();
      const baseCommand = definition.baseCommand.toLowerCase();

      return !searchTerm || fullCommand.startsWith(searchTerm) || baseCommand.startsWith(searchTerm);
    });
  }, [commandDefinitions, inputValue]);

  useEffect(() => {
    setSelectedSuggestionIndex(0);
  }, [inputValue]);

  useEffect(() => {
    setIsAutocompleteVisible(true);
  }, [inputValue]);

  useEffect(() => {
    if (!isAutocompleteVisible || !suggestionsListRef.current || !inputValue.trim().startsWith('gtw')) {
      return;
    }

    const activeSuggestion = suggestionsListRef.current.querySelector<HTMLElement>(
      `[data-suggestion-index="${selectedSuggestionIndex}"]`
    );

    activeSuggestion?.scrollIntoView({
      block: 'nearest'
    });
  }, [inputValue, isAutocompleteVisible, selectedSuggestionIndex]);

  const applySuggestion = useCallback(
    (suggestionIndex: number) => {
      const selectedSuggestion = gtwSuggestions[suggestionIndex];

      if (!selectedSuggestion) {
        return;
      }

      setInputValue(`gtw ${selectedSuggestion.fullCommand}`);
      setIsAutocompleteVisible(false);
    },
    [gtwSuggestions]
  );

  const runCommand = useCallback(
    async (commandValue: string) => {
      const trimmedCommand = commandValue.trim();

      if (!trimmedCommand) {
        return;
      }

      if (!clientRef.current?.isConnected()) {
        appendEntry('error', t('gateway_details.console.messages.connection_not_ready'));
        return;
      }

      if (!isKnownGtwCommand(trimmedCommand, commandDefinitions)) {
        appendEntry('error', t('gateway_details.console.messages.unknown_gtw_command'));
        return;
      }

      setIsSendingCommand(true);
      setIsAutocompleteVisible(false);
      appendEntry('command', trimmedCommand);

      try {
        const brokerResponse = await clientRef.current.sendCommand({
          command: trimmedCommand,
          gatewayId
        });

        const formattedResponse = formatBrokerResponse(brokerResponse);
        if (formattedResponse) {
          appendEntry('response', formattedResponse);
        }

        if (normalizeSpaces(trimmedCommand) === 'gtw --help') {
          setCommandDefinitions(parseHelpCommands(formattedResponse));
        }

        setCommandHistory(previousHistory => [...previousHistory, trimmedCommand]);
        historyIndexRef.current = -1;
        setInputValue('');
      } catch (error) {
        appendEntry(
          'error',
          error instanceof Error ? error.message : t('gateway_details.console.messages.command_send_failed')
        );
      } finally {
        setIsSendingCommand(false);
      }
    },
    [appendEntry, commandDefinitions, gatewayId, t]
  );

  const handleReconnect = useCallback(async () => {
    setIsReconnecting(true);
    clientRef.current?.disconnect(t('gateway_details.console.messages.reconnect_requested'));
    setEntries([createConsoleEntry('system', t('gateway_details.console.messages.reconnecting'))]);
    setCommandDefinitions([]);

    try {
      await connectInBackground();
    } finally {
      setIsReconnecting(false);
    }
  }, [connectInBackground, t]);

  const decoratedSuggestions = useMemo(
    () =>
      gtwSuggestions.map(suggestion =>
        suggestion.baseCommand === '--help' && !suggestion.description
          ? {
              ...suggestion,
              description: t('gateway_details.console.messages.help_description')
            }
          : suggestion
      ),
    [gtwSuggestions, t]
  );

  const gatewayConnectionStatus = useMemo(
    () => getGatewayConnectionStatus(gatewayLastSeen),
    [gatewayLastSeen]
  );

  const displayStatus = useMemo(() => {
    if (gatewayConnectionStatus === GATEWAY_NOT_CONNECTED) {
      return {
        message: t('gateway_details.console.status.offline'),
        tone: 'offline' as GatewayStatusTone
      };
    }

    return status;
  }, [gatewayConnectionStatus, status, t]);

  const statusChipStyles = useMemo(() => {
    switch (displayStatus.tone) {
      case 'connected':
        return {
          backgroundColor: '#DDF6E8',
          color: '#146A3D'
        };
      case 'offline':
        return {
          backgroundColor: '#EEF1F5',
          color: '#556070'
        };
      case 'connecting':
        return {
          backgroundColor: '#FFF2CD',
          color: '#8A5A00'
        };
      case 'error':
        return {
          backgroundColor: '#FDE1E1',
          color: '#A12828'
        };
      default:
        return {
          backgroundColor: '#E5E7EB',
          color: '#4B5563'
        };
    }
  }, [displayStatus.tone]);

  return (
    <Box
      ref={consoleRootRef}
      sx={{
        overflow: 'visible',
        backgroundColor: 'white',
        boxShadow: 2,
        borderRadius: '8px',
        width: '100%',
        p: 3
      }}
      data-testid="gateway-console"
    >
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>{t('gateway_details.console.title')}</Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={displayStatus.message} size="small" sx={{ ...statusChipStyles, fontWeight: 700 }} />
            <Tooltip title={t('gateway_details.console.actions.reconnect')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleReconnect}
                  disabled={isReconnecting}
                  sx={{ border: '1px solid #D9DCE4', borderRadius: '10px' }}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('gateway_details.console.actions.clear_terminal')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setEntries([])}
                  sx={{ border: '1px solid #D9DCE4', borderRadius: '10px' }}
                >
                  <BackspaceOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            borderRadius: '8px',
            backgroundColor: CONSOLE_BACKGROUND,
            height: 360,
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 2.5 },
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Box
              ref={consoleViewportRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                pr: 1,
                fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace'
              }}
            >
              {entries.map(entry => (
                <Typography
                  key={entry.id}
                  component="pre"
                  sx={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.55,
                    color:
                      entry.tone === 'error'
                        ? '#FFB4B4'
                        : entry.tone === 'command'
                        ? '#BFE7FF'
                        : entry.tone === 'system'
                        ? '#CFD2DA'
                        : CONSOLE_FOREGROUND,
                    fontSize: 13.5
                  }}
                >
                  {entry.tone === 'command' ? `$ ${entry.value}` : entry.value}
                </Typography>
              ))}
            </Box>

            <Box sx={{ position: 'relative', overflow: 'visible' }}>
              {isAutocompleteVisible && gtwSuggestions.length > 0 && inputValue.trim().startsWith('gtw') && (
                <Box
                  ref={suggestionsListRef}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 'calc(100% + 10px)',
                    maxHeight: 320,
                    borderRadius: '10px',
                    backgroundColor: '#3F4048',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0px 14px 28px rgba(0, 0, 0, 0.28)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    zIndex: 10
                  }}
                >
                  {decoratedSuggestions.slice(0, 8).map((suggestion, index) => (
                    <Box
                      key={suggestion.fullCommand}
                      data-suggestion-index={index}
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        cursor: 'pointer',
                        backgroundColor:
                          selectedSuggestionIndex === index ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                        borderBottom:
                          index === Math.min(gtwSuggestions.length, 8) - 1
                            ? 'none'
                            : '1px solid rgba(255, 255, 255, 0.06)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)'
                        }
                      }}
                      onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      onMouseDown={event => {
                        event.preventDefault();
                        applySuggestion(index);
                      }}
                    >
                      <Typography sx={{ color: CONSOLE_FOREGROUND, fontSize: 13, fontFamily: 'inherit' }}>
                        gtw {suggestion.fullCommand}
                      </Typography>
                      {!!suggestion.description && (
                        <Typography sx={{ color: MUTED_FOREGROUND, fontSize: 11.5, mt: 0.4 }}>
                          {suggestion.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              <Box
                component="form"
                onSubmit={event => {
                  event.preventDefault();
                  runCommand(inputValue);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.16)',
                  backgroundColor: 'rgba(22, 23, 28, 0.28)',
                  px: 1.5,
                  py: 1.15
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: '#9BE7AE',
                    fontWeight: 700,
                    fontSize: 13.5,
                    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace'
                  }}
                >
                  $
                </Typography>
                <input
                  value={inputValue}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setInputValue(event.currentTarget.value)}
                  onKeyDown={event => {
                    if (event.key === 'Tab' && gtwSuggestions.length > 0) {
                      event.preventDefault();
                      applySuggestion(selectedSuggestionIndex);
                      return;
                    }

                    if (event.key === 'Escape') {
                      setIsAutocompleteVisible(false);
                      return;
                    }

                    if (event.key === 'ArrowDown' && gtwSuggestions.length > 0 && inputValue.trim().startsWith('gtw')) {
                      event.preventDefault();
                      setSelectedSuggestionIndex(previousIndex =>
                        previousIndex >= Math.min(gtwSuggestions.length, 8) - 1 ? 0 : previousIndex + 1
                      );
                      return;
                    }

                    if (event.key === 'ArrowUp') {
                      if (gtwSuggestions.length > 0 && inputValue.trim().startsWith('gtw')) {
                        event.preventDefault();
                        setSelectedSuggestionIndex(previousIndex =>
                          previousIndex <= 0 ? Math.min(gtwSuggestions.length, 8) - 1 : previousIndex - 1
                        );
                        return;
                      }

                      if (commandHistory.length > 0) {
                        event.preventDefault();
                        historyIndexRef.current =
                          historyIndexRef.current === -1
                            ? commandHistory.length - 1
                            : Math.max(historyIndexRef.current - 1, 0);
                        setInputValue(commandHistory[historyIndexRef.current] || '');
                      }
                      return;
                    }

                    if (event.key === 'ArrowDown' && commandHistory.length > 0) {
                      event.preventDefault();

                      if (historyIndexRef.current === -1) {
                        return;
                      }

                      const nextIndex = historyIndexRef.current + 1;

                      if (nextIndex >= commandHistory.length) {
                        historyIndexRef.current = -1;
                        setInputValue('');
                        return;
                      }

                      historyIndexRef.current = nextIndex;
                      setInputValue(commandHistory[nextIndex] || '');
                    }
                  }}
                  placeholder={
                    displayStatus.tone === 'connected'
                      ? t('gateway_details.console.placeholder.connected')
                      : t('gateway_details.console.placeholder.waiting_connection')
                  }
                  disabled={displayStatus.tone !== 'connected' || isSendingCommand}
                  autoComplete="off"
                  spellCheck={false}
                  style={{
                    flex: 1,
                    border: 0,
                    outline: 'none',
                    background: 'transparent',
                    color: CONSOLE_FOREGROUND,
                    fontSize: '13.5px',
                    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace'
                  }}
                />
                {isSendingCommand && <CircularProgress size={18} thickness={5} sx={{ color: '#D6DAE3' }} />}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
